import { useCallback, useEffect, useRef, useState } from "react";
import userService from "@/endpoints/userService";
import { isViewsV2Enabled } from "@/utils/viewDefinition/flag";
import { normalizeLegacyTokenToV2Module } from "@/pages/Reports/v2Adapter";

export interface RecentView {
  viewId: string;
  viewName: string;
  viewUrl?: string;
  /** Server-computed URL (Views V2). Absent on degraded rows and on legacy (V1) data. */
  url?: string;
}

interface CacheEntry {
  views: RecentView[];
  fetchedAt: number;
}

interface LoadResult {
  views: RecentView[];
  ok: boolean;
}

/** Longer than any plausible hover sweep, shorter than the interval at which recents change. */
export const RECENT_VIEWS_FRESH_MS = 30_000;

/** Module-scoped so the cache survives unpinning the nav, which unmounts the panel. */
const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<LoadResult>>();

/**
 * Bumped by invalidateRecentViews. A load() captures the epoch when it starts and only
 * writes its result to the cache if the epoch is unchanged, so a response that was already
 * in flight when an invalidation happened cannot repopulate the cache with stale data.
 */
let epoch = 0;

async function requestRecentViews(moduleToken: string): Promise<RecentView[]> {
  if (isViewsV2Enabled()) {
    // The route token is legacy vocabulary ("EMOTIONAL", "QUESTIONS"); V2 keys recents by
    // module ("imagery", "exclusivequestions"), so lowercasing alone returns the wrong list.
    const response = await userService.viewsV2.recent(
      normalizeLegacyTokenToV2Module(moduleToken),
    );
    if (!Array.isArray(response)) return [];
    return response.map((item) => ({
      viewId: item.id,
      viewName: item.name,
      url: item.url,
    }));
  }

  const response: unknown = await userService.views.getRecentViews(moduleToken);
  if (!Array.isArray(response)) return [];
  return response.map((item: Record<string, unknown>) => ({
    viewId: item.viewId as string,
    viewName: item.viewName as string,
    viewUrl: item.viewUrl as string | undefined,
    url: item.url as string | undefined,
  }));
}

function load(moduleToken: string): Promise<LoadResult> {
  const pending = inFlight.get(moduleToken);
  if (pending) return pending;

  const startEpoch = epoch;

  const tracked: Promise<LoadResult> = requestRecentViews(moduleToken)
    .then((views) => {
      if (epoch === startEpoch) {
        cache.set(moduleToken, { views, fetchedAt: Date.now() });
      }
      return { views, ok: true };
    })
    .catch(() => ({ views: [] as RecentView[], ok: false }))
    .finally(() => {
      // invalidateRecentViews may have already cleared this token's entry (or replaced it
      // with a newer in-flight request under the same key); only remove our own entry.
      if (inFlight.get(moduleToken) === tracked) {
        inFlight.delete(moduleToken);
      }
    });

  inFlight.set(moduleToken, tracked);
  return tracked;
}

export function invalidateRecentViews(moduleToken?: string): void {
  epoch += 1;
  if (!moduleToken) {
    cache.clear();
    inFlight.clear();
    return;
  }
  cache.delete(moduleToken);
  inFlight.delete(moduleToken);
}

interface RecentViewsState {
  views: RecentView[];
  isLoading: boolean;
  hasResolved: boolean;
  hasError: boolean;
}

const IDLE: RecentViewsState = {
  views: [],
  isLoading: false,
  hasResolved: false,
  hasError: false,
};

export function useRecentViews(moduleToken: string | undefined, enabled = true) {
  const [state, setState] = useState<RecentViewsState>(IDLE);
  const [revalidateCount, setRevalidateCount] = useState(0);
  const activeToken = useRef<string | undefined>(undefined);

  const refresh = useCallback(() => setRevalidateCount((n) => n + 1), []);

  useEffect(() => {
    if (!moduleToken || !enabled) {
      // activeToken is deliberately left alone: recording a token that was never fetched
      // makes the next enabled pass mistake a cold start for an unchanged token.
      setState(IDLE);
      return;
    }

    const tokenChanged = activeToken.current !== moduleToken;
    activeToken.current = moduleToken;

    const entry = cache.get(moduleToken);
    if (entry) {
      setState({
        views: entry.views,
        isLoading: false,
        hasResolved: true,
        hasError: false,
      });
      if (Date.now() - entry.fetchedAt < RECENT_VIEWS_FRESH_MS) return;
    } else if (tokenChanged) {
      setState({ views: [], isLoading: true, hasResolved: false, hasError: false });
    } else {
      setState((prev) => ({ ...prev, isLoading: !prev.hasResolved }));
    }

    let cancelled = false;
    load(moduleToken).then(({ views, ok }) => {
      if (cancelled || activeToken.current !== moduleToken) return;
      setState((prev) => {
        if (ok) return { views, isLoading: false, hasResolved: true, hasError: false };
        if (prev.hasResolved) return { ...prev, isLoading: false };
        return { views: [], isLoading: false, hasResolved: false, hasError: true };
      });
    });

    return () => {
      cancelled = true;
    };
  }, [moduleToken, enabled, revalidateCount]);

  return { ...state, refresh };
}
